import { keyframes } from "@emotion/react";
import tw from "twin.macro";

type Mode = "convergent" | "divergent";

type ProgressiveHorizontalArrowsProps = {
  /**
   * Animation mode (default: "convergent").
   *
   * - `"convergent"` — arrow tips point toward each other (→ ←), drawn from
   *   the outer edges inward.
   * - `"divergent"` — arrow tips point away from each other (← →), drawn
   *   from the center outward.
   */
  mode?: Mode;
  /** Stroke color (any valid CSS color, default: blue-500 @ 80%). */
  color?: string;
  /** Total duration of one animation cycle in seconds (default: 2). */
  durationSeconds?: number;
  className?: string;
};

/**
 * Pair of horizontal SVG arrows revealed progressively along the X axis via
 * `clip-path`, then faded out and restarted in a loop.
 *
 * Uses a clip-path mask rather than `stroke-dashoffset` to ensure:
 *   - perfectly symmetric arrow heads (both wings drawn in full),
 *   - no "round linecap" ghost artifacts during the loop reset.
 *
 * Each arrow SVG is aligned to the inner edge of its slot so that the tips
 * (convergent) or tails (divergent) meet close to the container centerline
 * with only a very small visual gap — regardless of the consumer container
 * aspect ratio.
 *
 * Pure & presentation-only: no state, no props mutation.
 */
const ProgressiveHorizontalArrows = ({
  mode = "convergent",
  color = "rgb(59 130 246 / 0.8)",
  durationSeconds = 2,
  className,
}: ProgressiveHorizontalArrowsProps) => {
  // Each arrow is authored pointing right. We mirror it horizontally when it
  // needs to point left in world coordinates. Combined with the clip-path
  // reveal (which animates left→right in local space), mirroring makes the
  // reveal play right→left in world space, i.e. from the tail toward the tip.
  //
  //   convergent (→ ←): left slot as-is,   right slot mirrored
  //   divergent  (← →): left slot mirrored, right slot as-is
  const leftMirrored = mode === "divergent";
  const rightMirrored = mode === "convergent";

  // Hug the inner edge of each slot so the tips (convergent) or tails
  // (divergent) meet near the container center with only a tiny gap.
  // Note: the mirror transform on the outer slot also flips the SVG's
  // preserveAspectRatio anchor, so the same anchor value can be used for
  // both slots in a given mode.
  const align = mode === "convergent" ? "xMaxYMid meet" : "xMinYMid meet";

  return (
    <div
      className={className}
      css={[tw`w-full h-full flex items-center justify-center`]}
    >
      <Arrow
        color={color}
        duration={durationSeconds}
        mirrored={leftMirrored}
        align={align}
      />
      <Arrow
        color={color}
        duration={durationSeconds}
        mirrored={rightMirrored}
        align={align}
      />
    </div>
  );
};

type ArrowProps = {
  color: string;
  duration: number;
  mirrored: boolean;
  align: string;
};

/**
 * Right-pointing SVG arrow, revealed from its left edge (tail) to its right
 * edge (tip). When `mirrored` is true the element is flipped horizontally;
 * because `clip-path` is resolved before `transform` in CSS, the reveal then
 * plays right-to-left in world coordinates — still tail-first.
 */
const Arrow = ({ color, duration, mirrored, align }: ArrowProps) => (
  <div
    aria-hidden="true"
    css={[
      tw`flex-1 h-full`,
      mirrored && { transform: "scaleX(-1)" },
      {
        clipPath: "inset(0 100% 0 0)",
        animation: `${reveal} ${duration}s ease-in-out infinite`,
      },
    ]}
  >
    <svg
      viewBox="0 0 240 100"
      preserveAspectRatio={align}
      tw="w-full h-full"
    >
      <g
        stroke={color}
        strokeWidth={14}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Shaft — horizontal line from the tail to the tip. */}
        <path d="M 10 50 L 230 50" />
        {/* Arrow head — symmetric chevron pointing right. */}
        <path d="M 202 22 L 230 50 L 202 78" />
      </g>
    </svg>
  </div>
);

/**
 * Reveals the clip window from the left (tail) to the right (tip) between
 * 0%→50%, holds fully open until 75%, fades out by 95%, then snaps the clip
 * shut while invisible (95%→100%) so the next loop starts cleanly.
 */
const reveal = keyframes`
  0%   { clip-path: inset(0 100% 0 0); opacity: 1; }
  50%  { clip-path: inset(0 0    0 0); opacity: 1; }
  75%  { clip-path: inset(0 0    0 0); opacity: 1; }
  95%  { clip-path: inset(0 0    0 0); opacity: 0; }
  100% { clip-path: inset(0 100% 0 0); opacity: 0; }
`;

export default ProgressiveHorizontalArrows;
